import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client/react";
import { AlertCircle, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import TopUpDialog from "@/components/dashboard/TopUpDialog";
import { GET_BILLING_INVOICES_CONNECTION, GET_CREDITS_WITH_BREAKDOWN, GET_SUBSCRIPTION } from "@/graphql/queries";
import { useToast } from "@/hooks/use-toast";
import { BillingHistoryTab, BillingInformationTab, CreditHistoryTab, ManagePlanTab } from "./credits";
import type {
  BillingCapabilities,
  CreditTransaction,
  Subscription,
  CreditsBreakdown,
  PlanConfig,
  PageInfo,
  SubscriptionPlan,
} from "./types";
import { PLAN_DISPLAY_NAMES } from "./credits/planCatalog";


function formatDateLabel(dateString?: string | null) {
  if (!dateString) return null;
  const normalized =
    dateString.includes("T") || dateString.includes(" ")
      ? dateString
      : `${dateString}T00:00:00`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

type CreditsTab = "overview" | "manage-plan" | "history" | "billing-info";

function normalizeCreditsTab(tabParam?: string | null, openPlanModal = false): CreditsTab {
  if (openPlanModal) return "manage-plan";
  if (tabParam === "manage-plan" || tabParam === "upgrade") return "manage-plan";
  if (tabParam === "history") return "history";
  if (tabParam === "billing-info") return "billing-info";
  return "overview";
}

interface CreditsSectionProps {
  organizationId?: string | null;
  isAdmin: boolean;
  subscription: Subscription | null;
  creditsBreakdown: CreditsBreakdown | null;
  planConfigs: PlanConfig[];
  billingCapabilities: BillingCapabilities | null;
  creditsLoading: boolean;
  transactions: CreditTransaction[];
  transactionsLoading: boolean;
  transactionsPageInfo: PageInfo;
  loadingMoreTransactions: boolean;
  handleLoadMoreTransactions: () => Promise<void>;
  showTopUp: boolean;
  setShowTopUp: (value: boolean) => void;
  handleTopUpComplete: () => void;
  billingContactName: string;
  setBillingContactName: (value: string) => void;
  billingContactEmail: string;
  setBillingContactEmail: (value: string) => void;
  updatingBillingContact: boolean;
  hasBillingContactChanges: boolean;
  handleSaveBillingContact: () => Promise<void>;
}

export function CreditsSection({
  organizationId,
  isAdmin,
  subscription,
  creditsBreakdown,
  planConfigs,
  billingCapabilities,
  creditsLoading,
  transactions,
  transactionsLoading,
  transactionsPageInfo,
  loadingMoreTransactions,
  handleLoadMoreTransactions,
  showTopUp,
  setShowTopUp,
  handleTopUpComplete,
  billingContactName,
  setBillingContactName,
  billingContactEmail,
  setBillingContactEmail,
  updatingBillingContact,
  hasBillingContactChanges,
  handleSaveBillingContact,
}: CreditsSectionProps) {
  const apolloClient = useApolloClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const portalReturnParam = searchParams.get("portalReturn") === "1";
  const paymentStatusParam = searchParams.get("payment_status");
  const tabParam = searchParams.get("creditsTab");
  const openPlanModalParam = searchParams.get("openPlanModal") === "1";
  const normalizedTab = normalizeCreditsTab(tabParam, openPlanModalParam);

  const [syncingBillingState, setSyncingBillingState] = useState(false);
  const [activeTab, setActiveTab] = useState<CreditsTab>(normalizedTab);

  const handleTabChange = (tab: CreditsTab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set("creditsTab", tab);
    params.delete("openPlanModal");
    setSearchParams(params);
  };

  useEffect(() => {
    setActiveTab(normalizedTab);
  }, [normalizedTab]);

  useEffect(() => {
    const needsNormalization =
      tabParam !== normalizedTab ||
      openPlanModalParam;

    if (!needsNormalization) return;

    const params = new URLSearchParams(searchParams);
    params.set("creditsTab", normalizedTab);
    params.delete("openPlanModal");
    setSearchParams(params, { replace: true });
  }, [normalizedTab, openPlanModalParam, searchParams, setSearchParams, tabParam]);

  useEffect(() => {
    if (!portalReturnParam) {
      setSyncingBillingState(false);
      return;
    }

    let cancelled = false;
    setSyncingBillingState(true);

    void (async () => {
      try {
        for (let attempt = 0; attempt < 5; attempt += 1) {
          await apolloClient.refetchQueries({
            include: [GET_SUBSCRIPTION, GET_CREDITS_WITH_BREAKDOWN, GET_BILLING_INVOICES_CONNECTION],
          });
          if (cancelled || attempt === 4) break;
          await new Promise((resolve) => { window.setTimeout(resolve, 1500); });
          if (cancelled) break;
        }
      } catch (error) {
        console.error("Failed to sync billing state after portal return:", error);
      } finally {
        if (!cancelled) {
          const params = new URLSearchParams(window.location.search);
          params.delete("portalReturn");
          setSearchParams(params, { replace: true });
          setSyncingBillingState(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [apolloClient, portalReturnParam, setSearchParams]);

  useEffect(() => {
    if (!paymentStatusParam) return;
    const params = new URLSearchParams(window.location.search);
    params.delete("payment_status");
    setSearchParams(params, { replace: true });
    if (paymentStatusParam === "success") {
      toast({ title: "Payment successful", description: "Your payment was processed successfully." });
    } else if (paymentStatusParam === "cancelled") {
      toast({ title: "Payment cancelled", description: "Your payment was cancelled. No charges were made.", variant: "destructive" });
    }
  }, [paymentStatusParam, setSearchParams, toast]);

  // Derived values
  const currentPlan: SubscriptionPlan = subscription?.plan || "PERSONAL";
  const currentBillingProvider = subscription?.billingProvider || null;
  const pendingBillingProvider = subscription?.pendingBillingProvider || null;
  const isPersonalPlan = currentPlan === "PERSONAL";
  const isPaidActivePlan =
    (currentPlan === "PRO" || currentPlan === "STUDIO") &&
    subscription?.status !== "CANCELLED" &&
    subscription?.status !== "EXPIRED";
  const isSubscriptionPlan = currentPlan !== "PERSONAL" && subscription?.status !== "EXPIRED";

  const subscriptionCredits = creditsBreakdown?.subscriptionCredits || 0;
  const prepaidCredits = creditsBreakdown?.prepaidCredits || 0;
  const billableAmountUsd = creditsBreakdown?.billableAmountUsd || 0;
  const billableCredits = creditsBreakdown?.billableCredits || 0;
  const monthlyAllocation = creditsBreakdown?.monthlyAllocation || 0;
  const usedThisMonth = creditsBreakdown?.usedThisMonth || 0;
  const overageLockActive = Boolean(creditsBreakdown?.overageLockActive);
  const billingCycleEnd = creditsBreakdown?.billingCycleEnd;
  const billingCycleStartAt = subscription?.billingCycleStart || subscription?.createdAt || null;
  const rolloverCredits = Math.max(subscription?.rolloverCredits || 0, 0);
  const priceUsd = subscription?.priceUsd || 0;
  const nextInvoiceAmount = priceUsd + billableAmountUsd;

  const totalCreditsRemaining = useMemo(
    () => Math.max(subscriptionCredits, 0) + Math.max(prepaidCredits, 0),
    [subscriptionCredits, prepaidCredits],
  );
  const creditsDisplayBalance = isPersonalPlan ? Math.max(prepaidCredits, 0) : totalCreditsRemaining;
  const rolloverCreditsRemaining = Math.min(rolloverCredits, Math.max(subscriptionCredits, 0));
  const planCreditsRemaining = Math.max(Math.max(subscriptionCredits, 0) - rolloverCreditsRemaining, 0);
  const cycleUsageBreakdown = useMemo(() => {
    const billingCycleStart = billingCycleStartAt ? new Date(billingCycleStartAt) : null;
    if (isPersonalPlan || !billingCycleStart || Number.isNaN(billingCycleStart.getTime())) {
      return {
        planUsed: 0,
        prepaidUsed: 0,
        billableUsed: Math.max(billableCredits, 0),
      };
    }

    const usageSinceCycleStart = transactions.reduce(
      (totals, transaction) => {
        if (transaction.transactionType !== "debit") return totals;
        if (transaction.description?.toLowerCase().includes("applied to outstanding overage")) return totals;

        const createdAt = new Date(transaction.createdAt);
        if (Number.isNaN(createdAt.getTime()) || createdAt < billingCycleStart) {
          return totals;
        }

        const amount = Math.abs(transaction.amount);
        switch (transaction.creditSource) {
          case "SUBSCRIPTION":
            totals.subscriptionUsed += amount;
            break;
          case "PREPAID":
            totals.prepaidUsed += amount;
            break;
          default:
            break;
        }

        return totals;
      },
      {
        subscriptionUsed: 0,
        prepaidUsed: 0,
      },
    );

    return {
      planUsed: Math.min(usageSinceCycleStart.subscriptionUsed, monthlyAllocation),
      prepaidUsed: usageSinceCycleStart.prepaidUsed,
      billableUsed: Math.max(billableCredits, 0),
    };
  }, [billableCredits, billingCycleStartAt, isPersonalPlan, monthlyAllocation, transactions]);

  const billingCycleEndDate = formatDateLabel(billingCycleEnd) || "—";
  const billingCycleDaysRemaining = useMemo(() => {
    if (!billingCycleEnd) return null;
    const parsed = new Date(`${billingCycleEnd}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return Math.max(0, Math.ceil((parsed.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  }, [billingCycleEnd]);
  const billingCycleDaysRemainingText =
    billingCycleDaysRemaining === null
      ? null
      : billingCycleDaysRemaining === 1
        ? "1 day remaining"
        : `${billingCycleDaysRemaining} days remaining`;
  const nextBillingDate = useMemo(() => {
    if (!billingCycleEnd) return "—";
    const parsed = new Date(`${billingCycleEnd}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return "—";
    parsed.setDate(parsed.getDate() + 1);
    return parsed.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [billingCycleEnd]);
  const scheduledChange = subscription?.scheduledChange || null;
  const cancellationEffectiveText = useMemo(
    () =>
      formatDateLabel(scheduledChange?.effectiveOn) ||
      formatDateLabel(subscription?.expiresAt) ||
      "your next renewal",
    [scheduledChange?.effectiveOn, subscription?.expiresAt],
  );

  const cancellationBanner = useMemo(() => {
    if (scheduledChange?.status === "PENDING") {
      return {
        title: "Cancellation scheduled in billing",
        body: `Your ${PLAN_DISPLAY_NAMES[currentPlan]} plan stays active until ${cancellationEffectiveText}. Use billing to keep your current plan.`,
        action: "Manage cancellation",
      };
    }
    if (subscription?.status === "CANCELLED") {
      return {
        title: "Subscription cancelled",
        body: `Your paid plan remains usable until ${cancellationEffectiveText}. Any unpaid invoice will keep new sessions blocked until it is settled.`,
        action: "Open billing portal",
      };
    }
    return null;
  }, [scheduledChange?.status, subscription?.status, currentPlan, cancellationEffectiveText]);

  const canTopUp = Boolean(billingCapabilities?.prepaidTopupEnabled);

  const tabs = [
    { id: "overview" as const, label: "Overview" },
    { id: "manage-plan" as const, label: "Manage Plan" },
    { id: "history" as const, label: "Credit History" },
    { id: "billing-info" as const, label: "Billing Information" },
  ];

  return (
    <div className="space-y-8">
      {/* Plan header */}
      <div className="border-b border-border pb-4">
        <div className="flex flex-wrap gap-10">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground underline underline-offset-4">
              Current Plan
            </p>
            <p className="mt-1 text-base font-semibold">{PLAN_DISPLAY_NAMES[currentPlan]}</p>
          </div>
          {!isPersonalPlan && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground underline underline-offset-4">
                Next Billing Cycle
              </p>
              <p className="mt-1 text-base font-semibold">{nextBillingDate}</p>
            </div>
          )}
        </div>
      </div>

      {/* Syncing banner */}
      {syncingBillingState && !cancellationBanner && (
        <div className="rounded-2xl border border-sky-500/25 bg-sky-500/10 px-5 py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-sky-400" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-sky-300">Syncing billing changes</p>
              <p className="text-sm text-muted-foreground">
                Recent subscription changes can take a few seconds to appear.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overage lock banner */}
      {overageLockActive && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-5 py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-red-500">Payment required to resume streaming</p>
              <p className="text-sm text-muted-foreground">
                Your account has unpaid billable dues. Clear pending dues to start new sessions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Top-level tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={`pb-4 px-1 text-base font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "overview" && (
        <div className="space-y-8">
          {/* Usage stats */}
          <section>
            <h2 className="mb-4 text-xl font-semibold">Usage</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {/* Credits remaining */}
              <div className="rounded-lg border border-border bg-card px-5 py-4">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {isPersonalPlan ? "Available Credits" : "Available Credits"}
                </p>
                <div className="mt-3 flex items-end gap-3">
                  <p className="text-4xl font-semibold text-primary">
                    {creditsLoading ? "—" : creditsDisplayBalance.toLocaleString()}
                  </p>
                  {!isPersonalPlan && !creditsLoading && billingCycleDaysRemainingText ? (
                    <p className="pb-1 text-xs text-muted-foreground">{billingCycleDaysRemainingText}</p>
                  ) : null}
                </div>
                {!isPersonalPlan && !creditsLoading && (
                  <div className="mt-3 space-y-2 border-t border-border pt-3">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{PLAN_DISPLAY_NAMES[currentPlan]} Subscription</span>
                      <span className="font-medium">{planCreditsRemaining.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Rollover</span>
                      <span className="font-medium">{rolloverCreditsRemaining.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Pay As You Go</span>
                      <span className="font-medium">{Math.max(prepaidCredits, 0).toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Credits used this cycle */}
              {!isPersonalPlan && (
                <div className="rounded-lg border border-border bg-card px-5 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Used This Billing Cycle
                  </p>
                  <p className="mt-3 text-4xl font-semibold text-primary">
                    {creditsLoading ? "—" : usedThisMonth.toLocaleString()}
                  </p>
                  {!creditsLoading && (
                    <div className="mt-3 space-y-2 border-t border-border pt-3">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{PLAN_DISPLAY_NAMES[currentPlan]} Subscription</span>
                        <span className="font-medium">{cycleUsageBreakdown.planUsed.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Additional</span>
                        <span className="font-medium">{cycleUsageBreakdown.billableUsed.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Pay As You Go</span>
                        <span className="font-medium">{cycleUsageBreakdown.prepaidUsed.toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Next invoice */}
              {!isPersonalPlan && (
                <div className="flex h-full flex-col rounded-lg border border-border bg-card px-5 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Next Invoice
                  </p>
                  <p className="mt-3 text-4xl font-semibold text-primary">
                    {creditsLoading ? "—" : `$${nextInvoiceAmount.toFixed(2)}`}
                  </p>
                  <p className="mt-auto pt-3 text-xs text-muted-foreground">
                    Billing cycle ends {billingCycleEndDate}
                  </p>
                </div>
              )}

              {/* Personal plan: top up card */}
              {isPersonalPlan && (
                <div className="rounded-lg border border-border bg-card px-5 py-4 sm:col-span-2 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">Prepaid credits never expire</p>
                    <p className="text-sm text-muted-foreground">Top up anytime and use whenever you need.</p>
                  </div>
                  <Button
                    onClick={() => { if (canTopUp) setShowTopUp(true); }}
                    disabled={!billingCapabilities?.prepaidTopupEnabled}
                    size="sm"
                  >
                    <ArrowUpRight className="mr-2 h-4 w-4" />
                    Top up
                  </Button>
                </div>
              )}
            </div>
          </section>

          {/* Current billing period breakdown */}
          {!isPersonalPlan && isSubscriptionPlan && (
            <section>
              <h2 className="mb-4 text-xl font-semibold">Billing Cycle Summary</h2>
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        Credits
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      <td className="px-4 py-3">{PLAN_DISPLAY_NAMES[currentPlan]}: Monthly subscription</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {monthlyAllocation.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">${priceUsd.toFixed(2)}</td>
                    </tr>
                    <tr>
                        <td className="px-4 py-3">
                          {PLAN_DISPLAY_NAMES[currentPlan]}: Additional credits (overage)
                        </td>
                        <td className="px-4 py-3 text-right text-muted-foreground">
                          {billableCredits.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {billableAmountUsd > 0 ? `$${billableAmountUsd.toFixed(2)}` : "$0.00"}
                        </td>
                      </tr>
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-border bg-muted/40">
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        Total:
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        ${nextInvoiceAmount.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          )}

          {/* Statements */}
          <section>
            <div className="mb-4">
              <h2 className="text-xl font-semibold">Statements</h2>
            </div>
            <BillingHistoryTab billingCapabilities={billingCapabilities} showPortalButton={false} />
          </section>
        </div>
      )}

      {activeTab === "manage-plan" && (
        <ManagePlanTab
          currentPlan={currentPlan}
          currentBillingProvider={currentBillingProvider || "STRIPE"}
          pendingBillingProvider={pendingBillingProvider}
          billingProviderSwitchAt={subscription?.billingProviderSwitchAt}
          currentMonthlyCredits={Math.max(monthlyAllocation, subscription?.monthlyCredits || 0)}
          currentPersonalRooms={subscription?.personalRooms || 0}
          currentPriceUsd={priceUsd}
          billingCycleEnd={billingCycleEnd}
          currentSubscriptionCredits={subscriptionCredits}
          currentPlanCreditsRemaining={planCreditsRemaining}
          currentRolloverCredits={rolloverCreditsRemaining}
          currentPrepaidCredits={Math.max(prepaidCredits, 0)}
          planConfigs={planConfigs}
          billingCapabilities={billingCapabilities}
          organizationId={organizationId}
          cancellationBanner={cancellationBanner}
          showCancellationAction={isPaidActivePlan && currentBillingProvider !== "MANUAL" && !cancellationBanner}
        />
      )}

      {activeTab === "history" && (
        <CreditHistoryTab
          transactions={transactions}
          transactionsLoading={transactionsLoading}
          transactionsPageInfo={transactionsPageInfo}
          loadingMoreTransactions={loadingMoreTransactions}
          handleLoadMoreTransactions={handleLoadMoreTransactions}
        />
      )}

      {activeTab === "billing-info" && (
        <BillingInformationTab
          billingContactName={billingContactName}
          setBillingContactName={setBillingContactName}
          billingContactEmail={billingContactEmail}
          setBillingContactEmail={setBillingContactEmail}
          updatingBillingContact={updatingBillingContact}
          hasBillingContactChanges={hasBillingContactChanges}
          handleSaveBillingContact={handleSaveBillingContact}
        />
      )}

      <TopUpDialog
        open={showTopUp}
        onOpenChange={setShowTopUp}
        onTopUp={handleTopUpComplete}
        enabled={Boolean(billingCapabilities?.prepaidTopupEnabled)}
        unavailableReason={billingCapabilities?.prepaidTopupUnavailableReason}
      />
    </div>
  );
}
